// Object-driven sidebar panels for the Calendar app — pure JSON for the engine in
// ./engine. Panel `id` must match the `panelId` on its rail item in ../apps.ts and in
// apps/calendar/src/rail.ts. Projects already owns the ids `calendar` and `settings`,
// and the registry is flat (last writer wins), so these are prefixed.

import {
  SIDEBAR_SCHEMA_VERSION,
  type PanelConfig,
  type SidebarConfig,
} from '@clarix/shared-types';
import { CALENDAR_SHELL_PERM as P } from '../calendar-permissions';

/* ────────────────────────── Calendar ────────────────────────── */

const mainPanel: PanelConfig = {
  id: 'calendar-main',
  title: 'Calendar',
  headerRows: [
    {
      id: 'analytics',
      label: 'Analytics',
      icon: 'BarChart2',
      to: '/calendar/analytics',
      externalLinkIcon: 'subtle',
      // Same key as the route and the More tile, so the row never leads to a lock screen.
      visibleWhen: { permission: P.MEETINGS_ANALYTICS },
    },
  ],
  sections: [
    {
      id: 'schedule',
      divider: true,
      rows: [
        {
          id: 'show-availability',
          label: 'Show availability',
          icon: 'Clock',
          // The engine has no action rows; the page reads this URL intent.
          to: '/calendar?availability=1',
          visibleWhen: { permission: P.AVAILABILITY_SHARE },
        },
      ],
    },
    {
      id: 'today',
      label: 'Today',
      labelCountSuffix: 'upcoming',
      divider: true,
      loadingVariant: 'skeleton',
      emptyText: 'No upcoming meetings today',
      visibleWhen: { permission: P.MEETINGS_VIEW },
      rowsSource: { provider: 'calendarAppUpcoming' },
    },
  ],
};

/* ────────────────────────── Settings ────────────────────────── */

const settingsPanel: PanelConfig = {
  id: 'calendar-settings',
  title: 'Settings',
  sections: [
    {
      id: 'general',
      label: 'General',
      rows: [
        {
          id: 'preferences',
          label: 'Preferences',
          icon: 'SlidersHorizontal',
          to: '/calendar/settings/preferences',
        },
        {
          id: 'calendars',
          label: 'Calendars',
          icon: 'Layers',
          to: '/calendar/settings/calendars',
          visibleWhen: { permission: P.MEETINGS_VIEW },
        },
        {
          // Next to Calendars: both are workspace-wide lists; writing needs `rooms.manage`.
          id: 'equipment',
          label: 'Equipment',
          icon: 'Monitor',
          to: '/calendar/settings/equipment',
          visibleWhen: { permission: P.ROOMS_VIEW },
        },
        {
          id: 'availability',
          label: 'Availability',
          icon: 'Clock',
          to: '/calendar/settings/availability',
        },
        {
          // calendar-api drops these keys while the integrations module is off, so the row
          // never leads to "not in your plan"; a Guest holds none and has nothing to connect.
          id: 'integrations',
          label: 'Integrations',
          icon: 'Link2',
          to: '/calendar/settings/integrations',
          visibleWhen: {
            permission: [P.INTEGRATIONS_CONNECT, P.INTEGRATIONS_VIEW, P.INTEGRATIONS_MANAGE],
            permissionAny: true,
          },
        },
        {
          // No `visibleWhen`: every member reads the page; changing it needs `notification_settings.manage`.
          id: 'notifications',
          label: 'Notifications & Emails',
          icon: 'Bell',
          to: '/calendar/settings/notifications',
        },
      ],
    },
    {
      id: 'access',
      label: 'Access',
      divider: true,
      rows: [
        {
          id: 'roles-permissions',
          label: 'Roles & Permissions',
          icon: 'Shield',
          to: '/calendar/settings/roles-permissions',
          visibleWhen: { permission: P.ROLES_VIEW },
        },
        {
          id: 'audit',
          label: 'Audit log',
          icon: 'FileText',
          to: '/calendar/settings/audit',
          visibleWhen: { permission: P.AUDIT_VIEW },
        },
      ],
    },
  ],
};

export const calendarSidebar: SidebarConfig = {
  version: SIDEBAR_SCHEMA_VERSION,
  panels: [mainPanel, settingsPanel],
};
